import { Option } from "effect";
import type { Html } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import { busyAttributes, busySpinner } from "../../widgets/busy";
import { confirmDialog } from "../../widgets/dialog";
import type { Shared } from "../shared";
import { type ConfigKey, phaseLabel } from "./generator";
import { Message } from "./message";
import { type Model, isRunning } from "./model";

const CONFIG_FIELDS: ReadonlyArray<
	readonly [ConfigKey, string, string, number, number]
> = [
	["totalRecords", "gen-records", "Total records", 1, 1000],
	["editsPerRecord", "gen-edits", "Edits per record", 0, 20],
	["entriesPerRecordMin", "gen-entries-min", "Entries per record (min)", 0, 50],
	["entriesPerRecordMax", "gen-entries-max", "Entries per record (max)", 0, 50],
	["editsPerEntryMin", "gen-entry-edits-min", "Edits per entry (min)", 0, 20],
	["editsPerEntryMax", "gen-entry-edits-max", "Edits per entry (max)", 0, 20],
];

const PHASE_TEXT = {
	idle: "",
	"creating-records": phaseLabel("create-record"),
	"editing-records": phaseLabel("edit-record"),
	"creating-entries": phaseLabel("create-entry"),
	"editing-entries": phaseLabel("edit-entry"),
	done: "Complete",
	error: "Error",
} as const;

const dialogCopy = (
	model: Model,
): { title: string; message: string; confirmLabel: string } =>
	Option.match(model.pendingConfirm, {
		onNone: () => ({ title: "", message: "", confirmLabel: "Confirm" }),
		onSome: (action) => {
			switch (action) {
				case "credit":
				case "debit":
					return {
						title: "Confirm credit adjustment",
						message: `Record a ${action} of ${model.amount.trim()} for this circle?`,
						confirmLabel:
							action === "credit" ? "Add credits" : "Remove credits",
					};
				case "billing":
					return {
						title: "Confirm Deletion",
						message:
							"Delete all billing data for this circle? This clears ledger accounts, balances, and billing metadata.",
						confirmLabel: "Delete billing data",
					};
				case "cleanup":
					return {
						title: "Confirm cleanup",
						message: "Delete every event created by the history generator?",
						confirmLabel: "Clean up generated data",
					};
			}
		},
	});

/** v1 `Developer.tsx` (strings hardcoded there). Chaos and the Frontend Token field are recorded gaps. */
export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const g = model.generation;
	const running = isRunning(g);
	const total = Option.match(g.plan, {
		onNone: () => 0,
		onSome: (plan) => plan.steps.length,
	});
	const pct = total > 0 ? Math.round((g.progress / total) * 100) : 0;
	const blank = model.amount.trim() === "";
	const busy = Option.isSome(model.submitting);
	const feedback = (kind: "success" | "error", message: string): Html =>
		h.p(
			[
				h.Class(
					kind === "success" ? "text-sm text-success" : "text-sm text-error",
				),
			],
			[message],
		);
	const copy = dialogCopy(model);
	return h.div(
		[h.DataAttribute("testid", "page-Developer"), h.Class("max-w-4xl mx-auto")],
		[
			h.div(
				[
					h.Class(
						"space-y-4 rounded-2xl border border-base-300 bg-base-100 p-6 shadow-sm",
					),
				],
				[
					h.div(
						[],
						[
							h.strong([], ["Developer Options"]),
							h.div(
								[h.Class("flex items-center gap-2")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("p-2 bg-base-200 rounded-sm"),
											h.AriaLabel(
												model.developerEnabled
													? "Disable Developer Options"
													: "Enable Developer Options",
											),
											h.OnClick(Message.ClickedDeveloperOptions()),
										],
										[
											model.developerEnabled
												? "Disable Developer Options"
												: "Enable Developer Options",
										],
									),
								],
							),
							...(model.developerEnabled
								? [
										h.div(
											[h.Class("mt-2 space-y-3")],
											[
												h.div(
													[h.Class("flex flex-col gap-2")],
													[
														h.div(
															[h.Class("flex items-center justify-between")],
															[
																h.label(
																	[
																		h.Class("label-text"),
																		h.For("tag-input-mode"),
																	],
																	["Tag Input Mode"],
																),
																h.span(
																	[h.Class("badge badge-ghost")],
																	["Beta"],
																),
															],
														),
														h.select(
															[
																h.Id("tag-input-mode"),
																h.Class(
																	"select select-bordered w-full max-w-xs",
																),
																h.Value(model.tagInputVariant),
																h.OnChange((value) =>
																	Message.SelectedTagVariant({
																		variant:
																			value === "internal"
																				? "internal"
																				: "external",
																	}),
																),
															],
															[
																h.option(
																	[
																		h.Value("external"),
																		h.Selected(
																			model.tagInputVariant === "external",
																		),
																	],
																	["react-tag-autocomplete"],
																),
																h.option(
																	[
																		h.Value("internal"),
																		h.Selected(
																			model.tagInputVariant === "internal",
																		),
																	],
																	["Daisy tag input"],
																),
															],
														),
														h.p(
															[h.Class("text-sm text-base-content/70")],
															[
																"Use the Daisy-styled tag input to validate it as a drop-in replacement.",
															],
														),
													],
												),
											],
										),
									]
								: []),
						],
					),
					h.div(
						[],
						[
							h.strong([], ["Integrations Feature Flag"]),
							h.div(
								[h.Class("mt-2")],
								[
									h.div(
										[h.Class("flex items-start justify-between gap-4")],
										[
											h.div(
												[h.Class("space-y-1")],
												[
													h.span([h.Class("label-text")], ["Integrations"]),
													h.p(
														[h.Class("text-sm text-base-content/70")],
														[
															"Show integrations navigation and page while testing them.",
														],
													),
												],
											),
											h.label(
												[h.Class("flex items-center gap-3")],
												[
													h.input([
														h.Type("checkbox"),
														h.Class("toggle toggle-primary"),
														h.Checked(shared.integrationsEnabled),
														h.AriaLabel("Enable Integrations feature"),
														h.OnClick(
															Message.ToggledIntegrations({
																enabled: !shared.integrationsEnabled,
															}),
														),
													]),
													h.span(
														[h.Class("text-sm")],
														[
															shared.integrationsEnabled
																? "Enabled"
																: "Disabled",
														],
													),
												],
											),
										],
									),
								],
							),
						],
					),
					h.div(
						[],
						[
							h.strong([], ["Credits"]),
							h.div(
								[h.Class("mt-2 space-y-2")],
								[
									h.div(
										[h.Class("flex flex-col gap-2 md:flex-row")],
										[
											h.input([
												h.Type("text"),
												h.Class("input input-bordered md:w-48"),
												h.Value(model.amount),
												h.Placeholder("Amount (e.g. 25)"),
												h.AriaLabel("Amount"),
												h.OnInput((value) => Message.ChangedAmount({ value })),
											]),
											h.input([
												h.Type("text"),
												h.Class("input input-bordered flex-1"),
												h.Value(model.notes),
												h.Placeholder("Optional notes"),
												h.AriaLabel("Optional notes"),
												h.OnInput((value) => Message.ChangedNotes({ value })),
											]),
										],
									),
									h.div(
										[h.Class("flex gap-2")],
										[
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-primary"),
													h.AriaLabel("Add credits"),
													...busyAttributes(
														h,
														Option.contains(model.submitting, "credit"),
														busy || blank,
													),
													h.OnClick(
														Message.ClickedAdjust({ direction: "credit" }),
													),
												],
												[
													busySpinner(
														h,
														Option.contains(model.submitting, "credit"),
													),
													Option.contains(model.submitting, "credit")
														? "Adding..."
														: "Add credits",
												],
											),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-secondary"),
													h.AriaLabel("Remove credits"),
													...busyAttributes(
														h,
														Option.contains(model.submitting, "debit"),
														busy || blank,
													),
													h.OnClick(
														Message.ClickedAdjust({ direction: "debit" }),
													),
												],
												[
													busySpinner(
														h,
														Option.contains(model.submitting, "debit"),
													),
													Option.contains(model.submitting, "debit")
														? "Removing..."
														: "Remove credits",
												],
											),
										],
									),
									...Option.match(model.creditError, {
										onNone: () => [],
										onSome: (error) => [feedback("error", error)],
									}),
									...Option.match(model.lastAdjustment, {
										onNone: () => [],
										onSome: (a) => [
											h.div(
												[h.Class("text-sm text-success")],
												[
													`Recorded ${a.direction} of ${a.amount}. New balance: ${a.formattedBalance}. Entry ID: ${a.entryId}`,
												],
											),
										],
									}),
								],
							),
						],
					),
					h.div(
						[],
						[
							h.strong([], ["Billing Data"]),
							h.div(
								[h.Class("mt-2 space-y-2")],
								[
									h.p(
										[h.Class("text-sm text-base-content/70 max-w-xl")],
										[
											"Remove all ledger accounts, balances, and stored billing metadata for this circle. This action cannot be undone.",
										],
									),
									h.div(
										[h.Class("flex flex-wrap items-center gap-3")],
										[
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-error btn-sm"),
													h.AriaLabel("Delete billing data"),
													...busyAttributes(h, model.billingPending),
													h.OnClick(Message.ClickedDeleteBilling()),
												],
												[
													busySpinner(h, model.billingPending),
													model.billingPending
														? "Deleting..."
														: "Delete billing data",
												],
											),
											...Option.match(model.billingFeedback, {
												onNone: () => [],
												onSome: (f) => [
													h.span(
														[
															h.Class(
																f.kind === "success"
																	? "text-success text-sm"
																	: "text-error text-sm",
															),
														],
														[f.message],
													),
												],
											}),
										],
									),
								],
							),
						],
					),
					h.div(
						[],
						[
							h.strong([], ["Generate History Data"]),
							h.p(
								[h.Class("text-sm text-base-content/70 mt-1 mb-2")],
								[
									"Generate backdated records, entries, and edits across the past 30 days for testing time-travel and the heatmap.",
								],
							),
							h.div(
								[h.Class("space-y-3")],
								[
									h.div(
										[h.Class("grid grid-cols-2 gap-3")],
										CONFIG_FIELDS.map(([key, id, label, min, max]) =>
											h.div(
												[],
												[
													h.label(
														[h.Class("label"), h.For(id)],
														[h.span([h.Class("label-text")], [label])],
													),
													h.input([
														h.Id(id),
														h.Type("number"),
														h.Min(String(min)),
														h.Max(String(max)),
														h.Class("input input-bordered input-sm w-full"),
														h.Value(String(g.config[key])),
														h.Disabled(running),
														h.OnChange((value) =>
															Message.ChangedGenerationConfig({
																key,
																value: Number(value),
															}),
														),
													]),
												],
											),
										),
									),
									...(running
										? [
												h.div(
													[h.Class("space-y-1")],
													[
														h.div(
															[h.Class("flex justify-between text-sm")],
															[
																h.span([], [PHASE_TEXT[g.phase]]),
																h.span(
																	[],
																	[`${g.progress}/${total} (${pct}%)`],
																),
															],
														),
														h.progress([
															h.Class("progress progress-primary w-full"),
															h.Value(String(g.progress)),
															h.Max(String(total)),
														]),
													],
												),
											]
										: []),
									h.div(
										[h.Class("flex flex-wrap gap-2")],
										[
											running
												? h.button(
														[
															h.Type("button"),
															h.Class("btn btn-warning btn-sm"),
															h.AriaLabel("Cancel generation"),
															h.OnClick(Message.ClickedCancelGeneration()),
														],
														["Cancel"],
													)
												: h.button(
														[
															h.Type("button"),
															h.Class("btn btn-primary btn-sm"),
															h.AriaLabel("Generate history data"),
															h.OnClick(Message.ClickedGenerate()),
														],
														["Generate History"],
													),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-error btn-sm btn-outline"),
													h.AriaLabel("Clean up generated data"),
													...busyAttributes(h, g.cleaning, running),
													h.OnClick(Message.ClickedCleanup()),
												],
												[
													busySpinner(h, g.cleaning),
													g.cleaning
														? "Cleaning up..."
														: "Clean up generated data",
												],
											),
										],
									),
									...Option.match(g.error, {
										onNone: () => [],
										onSome: (error) => [feedback("error", error)],
									}),
									...Option.match(g.success, {
										onNone: () => [],
										onSome: (success) => [feedback("success", success)],
									}),
								],
							),
						],
					),
					confirmDialog(h, {
						slotId: "confirm-developer",
						model: model.dialog,
						toParentMessage: (message) => Message.GotDialogMessage({ message }),
						title: copy.title,
						message: copy.message,
						confirmLabel: copy.confirmLabel,
						cancelLabel: "Cancel",
						onConfirm: Message.ClickedConfirm(),
						isPending: busy || model.billingPending || g.cleaning,
					}),
				],
			),
		],
	);
});
